import {
  PostalAddressOption,
  TermsAndConditions,
} from 'scenes/common/flows/policyDetails/types';
import { PolicyIssued, YesNoAnswer } from 'scenes/common/types';
import {
  MotorFunnelState,
  PolicyHolder,
  VehicleModification,
  SpecialDetail,
  IsInsuredSincePossess,
} from 'scenes/motor/types';

export const getNumberOfPolicyHolders = (state: MotorFunnelState): number =>
  state.policyDetails.policyHolders.length;

export const isPolicyIssuedToPerson = (state: MotorFunnelState): boolean =>
  state.policyDetails.policyIssued === PolicyIssued.Person;

export const isPolicyIssuedToBusiness = (state: MotorFunnelState): boolean =>
  state.policyDetails.policyIssued === PolicyIssued.Business;

export const getPolicyHolders = (state: MotorFunnelState): PolicyHolder[] =>
  state.policyDetails.policyHolders;

export const getSelectedPolicyHolders = (
  state: MotorFunnelState
): PolicyHolder[] =>
  getPolicyHolders(state).filter((policyHolder) => policyHolder.isSelected);

export const sameOvernightAndPostalAddress = (
  state: MotorFunnelState
): boolean =>
  state.policyDetails.postalAddressOption === PostalAddressOption.MainAddress;

export const isPostalAddressLockedBagGpo = (state: MotorFunnelState): boolean =>
  state.policyDetails.postalAddressOption ===
  PostalAddressOption.LockedBagGpoAddress;

export const isPostalAddressDifferentManual = (
  state: MotorFunnelState
): boolean => state.policyDetails.isPostalAddressDifferentManual ?? false;

export const getBusinessContactName = (state: MotorFunnelState) => ({
  firstName: state.policyDetails.businessContactFirstName,
  lastName: state.policyDetails.businessContactLastName,
});

export const getBusinessNameAndAbn = (state: MotorFunnelState) => ({
  businessName: state.policyDetails.businessName,
  abn: state.policyDetails.abn,
});

export const getBusinessRegisteredForGst = (
  state: MotorFunnelState
): YesNoAnswer =>
  state.policyDetails.isBusinessRegisteredForGst ?? YesNoAnswer.Unspecified;

export const hasBusinessRegisteredForGst = (state: MotorFunnelState): boolean =>
  state.policyDetails.isBusinessRegisteredForGst === YesNoAnswer.Yes;

export const getContactEmailAddress = (state: MotorFunnelState): string =>
  state.policyDetails.contactEmailAddress ?? '';

export const getContactPhoneNumber = (state: MotorFunnelState): string =>
  state.policyDetails.contactPhoneNumber ?? '';

export const getPostalAddress = (state: MotorFunnelState) =>
  state.policyDetails.postalAddress;

export const getLockedBoxAddress = (state: MotorFunnelState) =>
  state.policyDetails.lockedBoxAddress;

export const getVehicleModifications = (
  state: MotorFunnelState
): VehicleModification[] => state.policyDetails.vehicleModifications ?? [];

export const getHasModifications = (state: MotorFunnelState): YesNoAnswer =>
  state.policyDetails.hasModifications ?? YesNoAnswer.Unspecified;

export const hasModifications = (state: MotorFunnelState): boolean =>
  getHasModifications(state) === YesNoAnswer.Yes;

export const getPolicyIssued = (state: MotorFunnelState): PolicyIssued =>
  state.policyDetails.policyIssued ?? PolicyIssued.Unspecified;

export const getPrimaryPolicyHolderPersonalContact = (
  state: MotorFunnelState
) => ({
  personalContactFirstName: state.policyDetails.personalContactFirstName,
  personalContactLastName: state.policyDetails.personalContactLastName,
});

export const hasAdditionalSelectedPolicyHolders = (state: MotorFunnelState) => {
  const primaryPolicyHolder = getPrimaryPolicyHolderPersonalContact(state);

  return getSelectedPolicyHolders(state).some(
    (ph) =>
      ph.firstName !== primaryPolicyHolder.personalContactFirstName &&
      ph.lastName !== primaryPolicyHolder.personalContactLastName
  );
};

export const getPostalAddressOption = (
  state: MotorFunnelState
): PostalAddressOption =>
  state.policyDetails.postalAddressOption ?? PostalAddressOption.MainAddress;

export const getHasPreExistingDamage = (state: MotorFunnelState): YesNoAnswer =>
  state.policyDetails.hasPreExistingDamage ?? YesNoAnswer.Unspecified;

export const hasPreExistingDamage = (state: MotorFunnelState): boolean =>
  getHasPreExistingDamage(state) === YesNoAnswer.Yes;

export const getSpecialDetails = (state: MotorFunnelState): SpecialDetail[] =>
  state.policyDetails.specialDetails ?? [];

export const getHasSpecialFeatures = (state: MotorFunnelState): YesNoAnswer =>
  state.policyDetails.hasSpecialFeatures ?? YesNoAnswer.Unspecified;

export const hasSpecialFeatures = (state: MotorFunnelState): boolean =>
  getHasSpecialFeatures(state) === YesNoAnswer.Yes;

export const getSelectedSpecialFeatures = (state: MotorFunnelState): string =>
  state.policyDetails.specialFeatures ?? '';

export const getIsRegoKnown = (state: MotorFunnelState): YesNoAnswer =>
  state.policyDetails.isRegoKnown ?? YesNoAnswer.Unspecified;

export const isRegoKnown = (state: MotorFunnelState): boolean =>
  getIsRegoKnown(state) === YesNoAnswer.Yes;

export const getMissingLicencePlate = (state: MotorFunnelState): string =>
  state.policyDetails.missingLicencePlate ?? '';

export const getLicencePlateNumberIfNotLookedUp = (
  state: MotorFunnelState
): string => (isRegoKnown(state) ? getMissingLicencePlate(state) : '');

export const getStereoCost = (state: MotorFunnelState): string =>
  state.policyDetails.stereoCost ?? '0';

export const getTermsAndConditions = (
  state: MotorFunnelState
): TermsAndConditions => state.policyDetails.termsAndCondition;

export const getTermsAndConditionsForSmallBusiness = (
  state: MotorFunnelState
): TermsAndConditions => state.policyDetails.termsAndConditionForSmallBusiness;

export const hasAgreedTermsAndConditions = (state: MotorFunnelState): boolean =>
  getTermsAndConditions(state) === TermsAndConditions.Accepted;

export const hasAgreedTermsAndConditionsForSmallBusiness = (
  state: MotorFunnelState
): boolean =>
  getTermsAndConditionsForSmallBusiness(state) === TermsAndConditions.Accepted;

export const getInsuredSincePossessed = (
  state: MotorFunnelState
): IsInsuredSincePossess =>
  state.policyDetails.isInsuredSincePossessed ??
  IsInsuredSincePossess.Unspecified;

export const hasInsuredSincePossess = (state: MotorFunnelState): boolean =>
  getInsuredSincePossessed(state) !== IsInsuredSincePossess.Unspecified;

export const isInsuredSincePossessed = (state: MotorFunnelState): boolean =>
  getInsuredSincePossessed(state) === IsInsuredSincePossess.Yes;
